import React from "react";
import { Rnd } from "react-rnd";
import {
  getSelectionOutlineOffset,
  toNumericStyleValue,
} from "../../Uitls/Util";
import {
  buildFillOnlySvgSrc,
  isShapeMatchingCanvasSize,
} from "../../Uitls/shapeStrokeOverlay";
import { useEditorMainPageContext } from "../contexts/EditorMainPageContext";

export const EditorCanvasShapeItem = ({ items, index }) => {
  const {
    playPauseRef,
    isAspectRatioLocked,
    handleComponentsDrag,
    handleComponentsDragStop,
    callOnResizeStop,
    updateFontOnResize,
    sendSelectedComponent,
    setFroalaIndex,
    setContainerID,
    setCurrentIdZindex,
    setVisibility,
    handleCtrlClick,
    setEditComponent,
    editComponentRef,
    handleDeleteKey,
    multiSelectedComps,
    selectedComponent,
    playPause,
    toggleAnimatedBanner,
    isDragged,
    canvasDimension,
  } = useEditorMainPageContext();

  if (items?.componentType !== "Shape") return null;

  const isFullCanvasShape = isShapeMatchingCanvasSize(
    items.componentStyles,
    canvasDimension,
  );

  // Full-canvas shapes paint their border in the elevated stroke overlay, which
  // already sits above the selection ring.
  const selectionOutlineOffset = isFullCanvasShape
    ? undefined
    : getSelectionOutlineOffset(items.componentStyles);

  return (
    <div>
      {items.textComponentType.addContent === "rectangleShape" && (
        <Rnd
          resizeHandleClasses={{
            topRight: "topAnchorRight",
            topLeft: "topAnchorLeft",
            bottomLeft: "bottomAnchorLeft",
            bottomRight: "bottomAnchorRight",
            top: "horizontalAnchorCenter",
            bottom: "horizontalAnchorCenter",
            left: "verticallAnchorCenter",
            right: "verticallAnchorCenter",
          }}
          disableDragging={
            items?.componentStyles?.hideBoolean ||
            items?.componentStyles?.lockBoolean ||
            !playPauseRef.current.animationCompleted
          }
          enableResizing={
            !items?.componentStyles?.hideBoolean &&
            !items?.componentStyles?.lockBoolean &&
            playPauseRef.current.animationCompleted
          }
          size={{
            width: items.componentStyles.width,
            height: items.componentStyles.height,
          }}
          scale={canvasDimension.scale}
          position={{
            x:
              toNumericStyleValue(items.componentStyles.left) +
              (parseInt(items.componentStyles.translateX) || 0),
            y:
              toNumericStyleValue(items.componentStyles.top) +
              (parseInt(items.componentStyles.translateY) || 0),
          }}
          lockAspectRatio={isAspectRatioLocked}
          onDrag={(e, d) => {
            handleComponentsDrag(d, items);
          }}
          onDragStop={(e, d) => {
            handleComponentsDragStop(e, d, items, index);
          }}
          onResizeStop={(e, direction, ref, delta, position) => {
            callOnResizeStop({
              id: items.id,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
              x: position.x - (parseInt(items.componentStyles.translateX) || 0),
              y: position.y - (parseInt(items.componentStyles.translateY) || 0),
            });
          }}
          onResize={(e, direction, ref, delta, position) => {
            updateFontOnResize({
              id: items.id,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
              x: position.x - (parseInt(items.componentStyles.translateX) || 0),
              y: position.y - (parseInt(items.componentStyles.translateY) || 0),
            });
          }}
          key={index}
          id={items.id}
          onClick={(e) => {
            sendSelectedComponent(e, index);
            setFroalaIndex(index);
            setContainerID(items.id);
            setCurrentIdZindex(e.currentTarget.id);
            setVisibility(false);
            handleCtrlClick(e, items);
            setEditComponent(undefined);
            editComponentRef.current = undefined;
          }}
          tabIndex="0"
          onKeyDown={(e) => handleDeleteKey(e)}
          className={
            `${
              multiSelectedComps.length < 2 && selectedComponent.id === items.id
                ? `${
                    !selectedComponent.componentStyles?.hideBoolean &&
                    "selectedComp hideOutline"
                  } div-dragresize-parent cs-container`
                : "div-dragresize-parent cs-container"
            }` +
            " " +
            `${
              multiSelectedComps.length > 1 && items.multiSelected === true
                ? "multipleSelection"
                : ""
            }`
          }
          style={{
            pointerEvents:
              (!playPause.animationCompleted ||
                items.componentStyles.hideBoolean) &&
              "none",
            zIndex: items.componentStyles.zIndex,
            outlineOffset: selectionOutlineOffset,
            webkitTransformOrigin: `${items.componentStyles.transformOriginX}% ${items.componentStyles.transformOriginY}% ${items.componentStyles.transformOriginZ}px`,
            transform: `rotate(${items.componentStyles.rotation}) rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
          }}
        >
          <div
            data-border-target="true"
            style={{
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              // Only apply opacity when the shape is hidden; otherwise parent owns opacity.
              ...(items?.componentStyles?.hideBoolean === true
                ? { opacity: items?.componentStyles?.opacity }
                : {}),
              pointerEvents: items?.componentStyles?.hideBoolean ? "none" : "",
              backgroundColor: items.componentStyles.backgroundColor,
              // Full-canvas shapes use an elevated stroke overlay; others keep border here.
              border: isFullCanvasShape
                ? undefined
                : items.componentStyles.border,
              paddingTop: items.componentStyles.paddingTop,
              paddingRight: items.componentStyles.paddingRight,
              paddingBottom: items.componentStyles.paddingBottom,
              paddingLeft: items.componentStyles.paddingLeft,
              borderTopLeftRadius: items.componentStyles.borderTopLeftRadius,
              borderTopRightRadius: items.componentStyles.borderTopRightRadius,
              borderBottomLeftRadius:
                items.componentStyles.borderBottomLeftRadius,
              borderBottomRightRadius:
                items.componentStyles.borderBottomRightRadius,
              transform: `rotate(${items.componentStyles.rotation}) rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
              aspectLock: isAspectRatioLocked,
            }}
            onMouseUp={(e) => {
              isDragged.mouseDown = false;
              sendSelectedComponent(e, index);
              setFroalaIndex(index);
              setContainerID(items.id);
              setCurrentIdZindex(e.currentTarget.id);
            }}
          >
            <div className="shapesOnCanvas"></div>
          </div>
        </Rnd>
      )}
      {items.textComponentType.addContent === "circleShape" && (
        <Rnd
          resizeHandleClasses={{
            topRight: "topAnchorRight",
            topLeft: "topAnchorLeft",
            bottomLeft: "bottomAnchorLeft",
            bottomRight: "bottomAnchorRight",
            top: "horizontalAnchorCenter",
            bottom: "horizontalAnchorCenter",
            left: "verticallAnchorCenter",
            right: "verticallAnchorCenter",
          }}
          disableDragging={
            items?.componentStyles?.hideBoolean ||
            items?.componentStyles?.lockBoolean ||
            !playPauseRef.current.animationCompleted
          }
          enableResizing={
            !items?.componentStyles?.hideBoolean &&
            !items?.componentStyles?.lockBoolean &&
            playPauseRef.current.animationCompleted
          }
          size={{
            width: items.componentStyles.width,
            height: items.componentStyles.height,
          }}
          scale={canvasDimension?.scale}
          position={{
            x:
              toNumericStyleValue(items.componentStyles.left) +
              (parseInt(items.componentStyles.translateX) || 0),
            y:
              toNumericStyleValue(items.componentStyles.top) +
              (parseInt(items.componentStyles.translateY) || 0),
          }}
          lockAspectRatio={isAspectRatioLocked}
          onDrag={(e, d) => {
            handleComponentsDrag(d, items);
          }}
          onDragStop={(e, d) => {
            handleComponentsDragStop(e, d, items, index);
          }}
          onResize={(e, direction, ref, delta, position) => {
            updateFontOnResize({
              id: items.id,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
              x: position.x - (parseInt(items.componentStyles.translateX) || 0),
              y: position.y - (parseInt(items.componentStyles.translateY) || 0),
            });
          }}
          key={index}
          id={items.id}
          className={
            `${
              multiSelectedComps.length < 2 && selectedComponent.id === items.id
                ? `${
                    !selectedComponent.componentStyles?.hideBoolean &&
                    "selectedComp"
                  } div-dragresize-parent cs-container`
                : "div-dragresize-parent cs-container"
            }` +
            " " +
            `${
              multiSelectedComps.length > 1 && items.multiSelected === true
                ? "multipleSelection"
                : ""
            }`
          }
          onClick={(e) => {
            sendSelectedComponent(e, index);
            setFroalaIndex(index);
            setContainerID(items.id);
            setCurrentIdZindex(e.currentTarget.id);
            setVisibility(false);
            handleCtrlClick(e, items);
            setEditComponent(undefined);
            editComponentRef.current = undefined;
          }}
          tabIndex="0"
          onKeyDown={(e) => handleDeleteKey(e)}
          style={{
            opacity:
              toggleAnimatedBanner === true ||
              items?.componentStyles?.hideBoolean === true
                ? items?.componentStyles?.opacity
                : items?.componentStyles?.compOpacity / 100,
            zIndex: items.componentStyles.zIndex,
            outlineOffset: selectionOutlineOffset,
            webkitTransformOrigin: `${items.componentStyles.transformOriginX}% ${items.componentStyles.transformOriginY}% ${items.componentStyles.transformOriginZ}px`,
            transform: `rotate(${items.componentStyles.rotation})rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
          }}
        >
          <div
            data-border-target="true"
            style={{
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              borderRadius: items.componentStyles.borderRadius,
              // Full-canvas shapes use an elevated stroke overlay; others keep border here.
              border: isFullCanvasShape
                ? undefined
                : items.componentStyles.border,
              backgroundColor: items.componentStyles.backgroundColor,
              visibility:
                items.id === selectedComponent.id &&
                items.componentStyles.opacity === 0
                  ? "hidden"
                  : "visible",
              paddingTop: items.componentStyles.paddingTop,
              transform: `rotate(${items.componentStyles.rotation}) rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
              ease: items.componentStyles.ease,
             // Only apply opacity when the shape is hidden; otherwise parent owns opacity.
             ...(items?.componentStyles?.hideBoolean === true
              ? { opacity: items?.componentStyles?.opacity }
              : {}),
            }}
            onMouseUp={(e) => {
              isDragged.mouseDown = false;
              sendSelectedComponent(e, index);
              setFroalaIndex(index);
              setContainerID(items.id);
              setCurrentIdZindex(e.currentTarget.id);
            }}
          >
            <div className="shapesOnCanvas"></div>
          </div>
          <div
            style={{
              width: "100%",
              height: "100%",
              paddingTop: items.componentStyles.paddingTop,
              transform: `rotate(${items.componentStyles.rotation}) rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
              ease: items.componentStyles.ease,
            }}
          ></div>
        </Rnd>
      )}
      {(items?.textComponentType?.addContent === "polygonShape" ||
        items?.textComponentType?.addContent === "triangleShape" ||
        items?.textComponentType?.addContent === "hexagonShape") && (
        <Rnd
          resizeHandleClasses={{
            topRight: "topAnchorRight",
            topLeft: "topAnchorLeft",
            bottomLeft: "bottomAnchorLeft",
            bottomRight: "bottomAnchorRight",
            top: "horizontalAnchorCenter",
            bottom: "horizontalAnchorCenter",
            left: "verticallAnchorCenter",
            right: "verticallAnchorCenter",
          }}
          disableDragging={
            items?.componentStyles?.hideBoolean ||
            items?.componentStyles?.lockBoolean ||
            !playPauseRef.current.animationCompleted
          }
          enableResizing={
            !items?.componentStyles?.hideBoolean &&
            !items?.componentStyles?.lockBoolean &&
            playPauseRef.current.animationCompleted
          }
          size={{
            width: items.componentStyles.width,
            height: items.componentStyles.height,
          }}
          position={{
            x:
              toNumericStyleValue(items.componentStyles.left) +
              (parseInt(items.componentStyles.translateX) || 0),
            y:
              toNumericStyleValue(items.componentStyles.top) +
              (parseInt(items.componentStyles.translateY) || 0),
          }}
          lockAspectRatio={isAspectRatioLocked}
          onDrag={(e, d) => {
            handleComponentsDrag(d, items);
          }}
          onDragStop={(e, d) => {
            handleComponentsDragStop(e, d, items, index);
          }}
          onResize={(e, direction, ref, delta, position) => {
            updateFontOnResize({
              id: items.id,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
              x: position.x - (parseInt(items.componentStyles.translateX) || 0),
              y: position.y - (parseInt(items.componentStyles.translateY) || 0),
            });
          }}
          key={index}
          id={items.id}
          className={
            `${
              multiSelectedComps.length < 2 && selectedComponent.id === items.id
                ? `${
                    !selectedComponent.selectedCompShape?.hideBoolean &&
                    "selectedComp"
                  } div-dragresize-parent cs-container`
                : "div-dragresize-parent cs-container"
            }` +
            " " +
            `${
              multiSelectedComps.length > 1 && items.multiSelected === true
                ? "multipleSelection"
                : ""
            }`
          }
          onClick={(e) => {
            sendSelectedComponent(e, index);
            setFroalaIndex(index);
            setContainerID(items.id);
            setCurrentIdZindex(e.currentTarget.id);
            setVisibility(false);
            handleCtrlClick(e, items);
            setEditComponent(undefined);
            editComponentRef.current = undefined;
          }}
          tabIndex="0"
          onKeyDown={(e) => handleDeleteKey(e)}
          style={{
            pointerEvents:
              (!playPause.animationCompleted ||
                items.componentStyles.hideBoolean) &&
              "none",
            zIndex: items.componentStyles.zIndex,
            opacity: items?.componentStyles?.hideBoolean === true && 0,
            webkitTransformOrigin: `${items.componentStyles.transformOriginX}% ${items.componentStyles.transformOriginY}% ${items.componentStyles.transformOriginZ}px`,
            transform: `rotate(${items.componentStyles.rotation}) rotateX(${items.componentStyles.rotationX}) rotateY(${items.componentStyles.rotationY}) scale(${items.componentStyles.scale})`,
          }}
        >
          <div
            className="shapesOnCanvas"
            style={{
              width: "100%",
              height: "100%",
              ease: items.componentStyles.ease,
              transform: items.componentStyles.scale,
              aspectLock: isAspectRatioLocked,
            }}
            onMouseUp={(e) => {
              isDragged.mouseDown = false;
              sendSelectedComponent(e, index);
              setFroalaIndex(index);
              setContainerID(items.id);
              setCurrentIdZindex(e.currentTarget.id);
            }}
          >
            {items?.textComponentType?.addContent === "triangleShape" && (
              <img
                className="triangleImage"
                src={
                  isFullCanvasShape
                    ? buildFillOnlySvgSrc(items.triImage)
                    : items.triImage
                }
                style={{
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  ...(items?.componentStyles?.hideBoolean === true
                    ? { opacity: items?.componentStyles?.opacity }
                    : {}),
                }}
                alt=""
              />
            )}
            {items?.textComponentType?.addContent === "polygonShape" && (
              <img
                className="pentagonImage"
                style={{
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  ...(items?.componentStyles?.hideBoolean === true
                    ? { opacity: items?.componentStyles?.opacity }
                    : {}),
                }}
                src={
                  isFullCanvasShape
                    ? buildFillOnlySvgSrc(items.pentImage)
                    : items.pentImage
                }
                alt=""
              />
            )}
            {items?.textComponentType?.addContent === "hexagonShape" && (
              <img
                className="img-fluid"
                style={{
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  ...(items?.componentStyles?.hideBoolean === true
                    ? { opacity: items?.componentStyles?.opacity }
                    : {}),
                }}
                src={
                  isFullCanvasShape
                    ? buildFillOnlySvgSrc(items.hexImage)
                    : items.hexImage
                }
                alt=""
              />
            )}
          </div>
        </Rnd>
      )}
    </div>
  );
};
